import { cx } from '@flexbyt/widget-preview';
import type { ElementType, ReactNode } from 'react';

/**
 * The type system, as components.
 *
 * ---------------------------------------------------------------------------
 * The scale is real
 * ---------------------------------------------------------------------------
 * `text-display-xl` through `text-micro` come from the shared tokens, and each
 * step carries its own line-height, letter-spacing and weight. That is the
 * thing that separates typography from "some text at various sizes": tracking
 * has to tighten as size grows and loosen as it shrinks, and a scale where
 * every step inherits one letter-spacing value looks wrong at both ends without
 * anybody being able to say why.
 *
 * ---------------------------------------------------------------------------
 * Level and size are separate arguments
 * ---------------------------------------------------------------------------
 * `<Heading level={2} size="display">` — because the heading level is a
 * document-structure decision and the size is a visual one, and tying them
 * together forces a choice between a sensible outline and a sensible page.
 *
 * The usual casualty is the outline: somebody needs a smaller heading, reaches
 * for `<h4>`, and the page now has an h1 followed by an h4 with nothing in
 * between, which is what a screen-reader user navigates by.
 */

const HEADING_SIZES = {
  /* The one h1 on a page. Responsive by construction: 52px on a laptop is
     confident, and on a 375px phone it is four words per line. */
  hero: 'text-display text-balance sm:text-display-lg lg:text-display-xl',
  /* Section headings. */
  display: 'text-display-sm text-balance sm:text-display lg:text-display-lg',
  /* Sub-sections, and the largest thing inside a card. */
  title: 'text-title',
  /* A label that has to behave like a heading in the outline. */
  label: 'text-body-sm font-semibold',
} as const;

export function Heading({
  children,
  level = 2,
  size = 'display',
  className,
  id,
}: {
  children: ReactNode;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  size?: keyof typeof HEADING_SIZES;
  className?: string;
  id?: string;
}) {
  const Tag = `h${level}` as ElementType;

  return (
    <Tag
      id={id}
      className={cx(
        HEADING_SIZES[size],
        'text-ink-950',
        // Optical alignment only at display sizes, where a capital's sidebearing
        // is wide enough to read as an indent against the copy beneath it.
        (size === 'hero' || size === 'display') && 'optical-left',
        className
      )}
    >
      {children}
    </Tag>
  );
}

/**
 * Body copy.
 *
 * `ink-600` rather than the `ink-900` of headings. The contrast step between a
 * heading and its paragraph is doing hierarchy work that a size change alone
 * does not — and 600 on white is 7.5:1, so it is a hierarchy that costs nothing
 * in legibility. `ink-500`, the next step down, is where muted text lives and
 * is the floor; anything lighter is decoration pretending to be text.
 */
export function Text({
  children,
  size = 'base',
  tone = 'muted',
  className,
  as: Tag = 'p',
}: {
  children: ReactNode;
  size?: 'lead' | 'base' | 'small';
  tone?: 'default' | 'muted' | 'subtle';
  className?: string;
  as?: ElementType;
}) {
  const sizes = {
    /* The paragraph directly under an h1. Larger, and the only place body copy
       is allowed to be. */
    lead: 'text-body-lg sm:text-title font-normal',
    base: 'text-body',
    small: 'text-body-sm',
  } as const;

  const tones = {
    default: 'text-ink-900',
    muted: 'text-ink-600',
    /* Timestamps, counts, fine print. Never a whole paragraph. */
    subtle: 'text-ink-500',
  } as const;

  return (
    <Tag className={cx(sizes[size], tones[tone], 'text-pretty', className)}>
      {children}
    </Tag>
  );
}

/**
 * A small uppercase signpost above a heading.
 *
 * Used sparingly and on purpose: it exists to say "this section is a different
 * kind of thing from the last one", and a page where every section has one has
 * said nothing.
 */
export function Eyebrow({
  children,
  className,
  tone = 'muted',
}: {
  children: ReactNode;
  className?: string;
  tone?: 'muted' | 'inverse';
}) {
  return (
    <p
      className={cx(
        'eyebrow',
        tone === 'inverse' ? 'text-grape-soft' : 'text-brand',
        className
      )}
    >
      {children}
    </p>
  );
}

/**
 * A form label, or any label doing a label's job outside a form.
 *
 * Same size and weight in both cases, because they are the same thing to a
 * reader: the name of the value beside them.
 */
export function Label({
  children,
  className,
  htmlFor,
}: {
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cx('block text-body-sm font-medium text-ink-800', className)}
    >
      {children}
    </label>
  );
}

/**
 * An editorial pull-quote.
 *
 * Deliberately *not* a review card. Review cards on this site are drawn by the
 * real widget renderer — that is the whole point of sharing it — and a second
 * component that also renders a review with an avatar and stars would be the
 * marketing-only copy the brief rules out.
 *
 * This is the other thing: a sentence lifted out of running text and set large,
 * the way a magazine sets one. No card, no border, no avatar. The quotation
 * marks are left off the visible text and carried by the markup, because a
 * typographic quote mark hanging in the margin is a nice detail and a literal
 * `"` in a heading is not.
 */
export function Quote({
  children,
  cite,
  className,
}: {
  children: ReactNode;
  /** Who said it. Rendered as the attribution line. */
  cite?: ReactNode;
  className?: string;
}) {
  return (
    <figure className={cx('max-w-text', className)}>
      <blockquote className="text-display-sm text-balance text-ink-950 sm:text-display">
        {children}
      </blockquote>
      {cite ? (
        <figcaption className="mt-5 text-body-sm text-ink-500">{cite}</figcaption>
      ) : null}
    </figure>
  );
}

/**
 * One phrase, marked.
 *
 * An oblique accent stroke tucked under the words — the loudest thing this
 * site's typography does, and therefore the thing it should do least often.
 *
 * ---------------------------------------------------------------------------
 * The rule that keeps it from becoming decoration
 * ---------------------------------------------------------------------------
 * Once per page, on the phrase that carries the page's argument. Emphasis is a
 * ratio: on a page where three headings are marked, none of them are. If a
 * second one feels necessary, the first one is probably on the wrong phrase.
 *
 * Mark a *phrase*, not a sentence. The stroke is `inline-block` so a marked run
 * never breaks across lines — which is a feature at two or three words and a
 * layout problem at ten.
 *
 * The styling lives in `app.css` as `.marked`, next to the note explaining why
 * it is a geometric bar rather than the hand-drawn squiggle this effect usually
 * gets.
 */
export function Mark({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <span className={cx('marked', className)}>{children}</span>;
}

/**
 * One phrase marked inside a headline that arrives as a string.
 *
 * ---------------------------------------------------------------------------
 * Why this exists alongside `<Mark>`
 * ---------------------------------------------------------------------------
 * Most headings on this site are literal JSX, and those wrap the phrase inline.
 * The data-driven pages cannot: a widget page renders `widget.headline` and an
 * integration page renders `source.headline`, so there is no JSX to wrap — the
 * emphasis has to be applied to a substring at render time.
 *
 * The phrase is passed in rather than guessed. Marking "a word near the end"
 * would put the stroke under whatever happened to be there, and a mark on the
 * wrong phrase is worse than no mark: it tells the reader the sentence's point
 * is something it is not.
 *
 * ---------------------------------------------------------------------------
 * It fails to plain text, never to a broken heading
 * ---------------------------------------------------------------------------
 * If the phrase is not in the text — copy edited, a record missing the field —
 * the headline renders exactly as written, unmarked. The alternative is a page
 * that throws, or one that marks a fragment of a word, and neither is worth
 * risking for a decoration. `indexOf` and not a regular expression, so a phrase
 * containing punctuation needs no escaping.
 */
export function MarkPhrase({ text, phrase }: { text: string; phrase?: string }) {
  const at = phrase ? text.indexOf(phrase) : -1;
  if (at === -1 || !phrase) return <>{text}</>;

  // Punctuation straight after the phrase travels with it (see below).
  const rest = text.slice(at + phrase.length);
  const [, stuck = '', after = rest] = /^([^\s]*)([\s\S]*)$/.exec(rest) ?? [];

  return (
    <>
      {text.slice(0, at)}
      {/*
        Inside the mark, not after it: `.marked` is an inline-block, a line may
        break at either edge of one, and a full stop left outside ended up alone
        on the line under the highlight.
      */}
      <Mark>
        {phrase}
        {stuck}
      </Mark>
      {after}
    </>
  );
}
